import { useRef, useState, type DragEvent } from "react";
import { Button } from "@/components/ui/Button";
import { useUploadMedia } from "@/features/media/hooks";

export function MediaUploader() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadMedia();
  const [dragging, setDragging] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  function submit(files: FileList | File[]) {
    const list = Array.from(files).filter((file) => file.type.startsWith("image/"));
    if (list.length === 0) return setMessage("Choose at least one image file.");
    setMessage(null);
    upload.mutate(list, {
      onError: (err) => setMessage(err instanceof Error ? err.message : "Upload failed."),
    });
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    submit(event.dataTransfer.files);
  }

  return (
    <div
      className={dragging ? "rounded-md border border-cyan-300 bg-cyan-300/10 p-5 shadow-neon" : "rounded-md border border-dashed border-cyan-300/30 bg-black/25 p-5"}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        className="hidden"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        multiple
        onChange={(event) => event.target.files && submit(event.target.files)}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-display text-lg font-semibold uppercase text-white">Upload Images</p>
          <p className="mt-1 text-sm text-content-secondary">Drag images here, or choose PNG, JPG, JPEG, WEBP, or safe SVG files.</p>
        </div>
        <Button type="button" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? "Uploading..." : "Choose Files"}
        </Button>
      </div>
      {message && <p className="mt-3 text-sm text-red-100">{message}</p>}
    </div>
  );
}
